import { Transaction } from "@/api/transactions";
import { cn } from "@/lib/utils";
import TransactionPagination from "./TransactionPagination";
import TransactionPaymentMethod from "./TransactionPaymentMethod";
import TransactionStatusBadge from "./TransactionStatusBadge";
import {
	formatTransactionAmount,
	getFormattedTransactionDate,
	getFormattedTransactionTime,
	getTransactionIcon,
	getTransactionIconContainerClassName,
} from "./utils";

interface TransactionListProps {
	data: Transaction[];
	totalRows: number;
	currentPage: number;
	perPage: number;
	onPageChange: (page: number) => void;
}

const DESKTOP_GRID_COLS =
	"md:grid md:grid-cols-[minmax(9rem,1.15fr)_minmax(11rem,1.5fr)_minmax(8rem,1.15fr)_minmax(5.5rem,7rem)_minmax(6.5rem,8.5rem)] md:items-center md:gap-x-6";

const DESKTOP_HEADER_CELL =
	"pb-3 text-sm font-semibold uppercase tracking-wide text-textLight";

const TransactionList = ({
	data,
	totalRows,
	currentPage,
	perPage,
	onPageChange,
}: TransactionListProps) => {
	return (
		<div className="flex w-full flex-col">
			<div className="flex flex-col gap-3 md:hidden">
				{data.map((transaction) => {
					const formattedDate = getFormattedTransactionDate(
						transaction.created_datetime,
					);
					const formattedTime = getFormattedTransactionTime(
						transaction.created_datetime,
					);
					const formattedAmount = formatTransactionAmount(
						transaction.transaction_type_name,
						transaction.amount,
					);

					return (
						<div
							key={transaction.transaction_id}
							className="flex flex-col gap-3 rounded-2xl border border-gray-100 p-4"
						>
							<div className="flex items-start justify-between gap-4">
								<div className="flex min-w-0 items-center gap-3">
									<div
										className={cn(
											"flex shrink-0 rounded-full p-3",
											getTransactionIconContainerClassName(
												transaction.transaction_type_name,
											),
										)}
									>
										{getTransactionIcon(transaction.transaction_type_name)}
									</div>
									<span className="text-lg font-bold text-textDark">
										{transaction.transaction_type_name}
									</span>
								</div>
								<div className="flex shrink-0 flex-col items-end gap-1.5">
									<span className="text-lg font-bold text-textDark">
										{formattedAmount}
									</span>
									<TransactionStatusBadge
										status={transaction.transaction_status}
										transactionType={transaction.transaction_type_name}
										size="md"
									/>
								</div>
							</div>

							<div className="flex flex-col gap-2.5 border-t border-gray-100 pt-3 pl-[3.25rem]">
								<div className="flex flex-col gap-0.5">
									<span className="text-base font-medium text-textLight">
										{formattedDate}
									</span>
									<span className="text-sm font-normal text-textLight">
										{formattedTime}
									</span>
								</div>
								<TransactionPaymentMethod
									cardType={transaction.card_type}
									last4={transaction.card_last4}
									className="text-base [&_svg]:size-5"
								/>
								<span className="text-sm font-medium text-textLight/80">
									ID: {transaction.transaction_id}
								</span>
							</div>
						</div>
					);
				})}
			</div>

			<div className="hidden md:flex md:flex-col">
				<div className={cn(DESKTOP_GRID_COLS, "")}>
					<span className={DESKTOP_HEADER_CELL}>Type</span>
					<span className={DESKTOP_HEADER_CELL}>Date</span>
					<span className={DESKTOP_HEADER_CELL}>Card / Bank Account</span>
					<span className={cn(DESKTOP_HEADER_CELL, "text-right")}>Amount</span>
					<span className={cn(DESKTOP_HEADER_CELL, "text-right")}>Status</span>
				</div>

				<div className="mt-1 flex flex-col">
					{data.map((transaction) => {
						const formattedDate = getFormattedTransactionDate(
							transaction.created_datetime,
						);
						const formattedTime = getFormattedTransactionTime(
							transaction.created_datetime,
						);
						const formattedAmount = formatTransactionAmount(
							transaction.transaction_type_name,
							transaction.amount,
						);

						return (
							<div
								key={transaction.transaction_id}
								className={cn(
									DESKTOP_GRID_COLS,
									"-mx-2 rounded-xl px-2 py-3.5 transition-colors hover:bg-primary-50/40",
								)}
							>
								<div className="flex min-w-0 items-center gap-3">
									<div
										className={cn(
											"flex shrink-0 rounded-full p-2.5",
											getTransactionIconContainerClassName(
												transaction.transaction_type_name,
											),
										)}
									>
										{getTransactionIcon(transaction.transaction_type_name)}
									</div>
									<div className="min-w-0">
										<span className="text-base font-bold text-textDark">
											{transaction.transaction_type_name}
										</span>
										<span className="mt-0.5 block text-sm font-medium text-textLight">
											ID: {transaction.transaction_id}
										</span>
									</div>
								</div>
								<div className="flex flex-col gap-0.5">
									<span className="text-base font-medium leading-snug text-textLight">
										{formattedDate}
									</span>
									<span className="text-sm font-normal text-textLight">
										{formattedTime}
									</span>
								</div>
								<div className="min-w-0">
									<TransactionPaymentMethod
										cardType={transaction.card_type}
										last4={transaction.card_last4}
										className="text-base [&_svg]:size-[1.125rem]"
									/>
								</div>
								<span className="text-right text-base font-bold text-textDark">
									{formattedAmount}
								</span>
								<div className="flex items-center justify-end">
									<TransactionStatusBadge
										status={transaction.transaction_status}
										transactionType={transaction.transaction_type_name}
										size="md"
									/>
								</div>
							</div>
						);
					})}
				</div>
			</div>

			<TransactionPagination
				itemsPerPage={perPage}
				currentPage={currentPage}
				totalRows={totalRows}
				onPageChange={onPageChange}
			/>
		</div>
	);
};

export default TransactionList;
